import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import defaultLogo from '../images/tranzcribe-logo-text-black.png';

interface AppSettings {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}

interface AppSettingsContextType {
  settings: AppSettings;
  loading: boolean;
  error: string | null;
  refetchSettings: () => Promise<void>;
  getLogoUrl: () => string;
}

const defaultSettings: AppSettings = {
  companyName: 'Tranzcribe',
  logoUrl: null,
  primaryColor: '#5D5FEF',
  secondaryColor: '#4C4DCD',
};

// Helper function to get cached settings from localStorage
const getCachedSettings = (): AppSettings => {
  try {
    const cached = localStorage.getItem('app-settings');
    if (cached) {
      const parsed = JSON.parse(cached);
      // Validate that all required fields exist
      if (parsed.companyName && parsed.primaryColor && parsed.secondaryColor) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to parse cached settings:', error);
  }
  return defaultSettings;
};

// Helper function to cache settings in localStorage
const cacheSettings = (settings: AppSettings) => {
  try {
    localStorage.setItem('app-settings', JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to cache settings:', error);
  }
};

const AppSettingsContext = createContext<AppSettingsContextType>({
  settings: getCachedSettings(),
  loading: true,
  error: null,
  refetchSettings: async () => {},
  getLogoUrl: () => defaultLogo,
});

export const useAppSettings = () => useContext(AppSettingsContext);

interface AppSettingsProviderProps {
  children: ReactNode;
}

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({ children }) => {
  // Initialize with cached settings to prevent flash
  const [settings, setSettings] = useState<AppSettings>(getCachedSettings());
  // Set initial loading state to false if we have cached settings
  const [loading, setLoading] = useState<boolean>(!localStorage.getItem('app-settings'));
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  
  // Prevent excessive fetching with a debounce mechanism
  const fetchSettings = useCallback(async (force = false) => {
    // Skip fetching if it was done in the last 10 seconds (unless force=true)
    const now = Date.now();
    if (!force && now - lastFetchTime < 10000) {
      return;
    }
    
    try {
      // Only show loading if we don't have cached settings
      if (!localStorage.getItem('app-settings')) {
        setLoading(true);
      }
      setError(null);
      
      const response = await axios.get(`${API_URL}/app-settings`, {
        withCredentials: true,
      });
      
      if (response.data) {
        const newSettings = {
          companyName: response.data.companyName || defaultSettings.companyName,
          logoUrl: response.data.logoUrl,
          primaryColor: response.data.primaryColor || defaultSettings.primaryColor,
          secondaryColor: response.data.secondaryColor || defaultSettings.secondaryColor,
        };
        setSettings(newSettings);
        cacheSettings(newSettings); // Cache the settings
      }
      
      setLastFetchTime(now);
    } catch (err) {
      console.error('Error fetching app settings:', err);
      setError('Failed to load application settings');
      // Don't revert to default settings if we have cached ones
      // Only use defaults if there's no cache
      if (!localStorage.getItem('app-settings')) {
        setSettings(defaultSettings);
      }
    } finally {
      setLoading(false);
    }
  }, [lastFetchTime]);

  // Get the full URL for the logo
  const getLogoUrl = (): string => {
    if (!settings.logoUrl) {
      return defaultLogo;
    }
    
    // If it's already a full URL, return it
    if (settings.logoUrl.startsWith('http')) {
      return settings.logoUrl;
    }
    
    // Otherwise, construct the URL using the API base URL
    return `${API_URL}${settings.logoUrl}`;
  };
  
  // Initial fetch on mount
  useEffect(() => {
    fetchSettings(true);
  }, []);
  
  // Handle visibility changes to prevent unnecessary reloading
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only fetch if the page becomes visible AND it's been more than 5 minutes
      if (document.visibilityState === 'visible' && Date.now() - lastFetchTime > 300000) {
        fetchSettings();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSettings, lastFetchTime]);

  // Provide a way to force refresh settings
  const refetchSettingsForced = async () => {
    await fetchSettings(true);
  };

  return (
    <AppSettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refetchSettings: refetchSettingsForced,
        getLogoUrl,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export default AppSettingsContext; 